/**
 * RESPONSIBILITY: which sermons a visitor's search and filters leave, newest first.
 *
 * Pure, so the rule is one place and testable. Within one filter, ANY picked value
 * matches — ticking a second series widens the list, which is what a second tick means.
 * Across filters, ALL must hold: a series AND a speaker narrows to that speaker's
 * sermons in that series. The search is words, every one of which must appear somewhere
 * in the sermon — title, scripture, speaker, series, special event or description.
 */
import type { SermonRow } from '@app/sections';

export interface SermonFilters {
  q: string;
  series: string[];
  speakers: string[];
  years: string[];
  occasions: string[];
}

export const NO_FILTERS: SermonFilters = { q: '', series: [], speakers: [], years: [], occasions: [] };

export const isFiltered = (f: SermonFilters): boolean =>
  Boolean(f.q.trim() || f.series.length || f.speakers.length || f.years.length || f.occasions.length);

export const yearOf = (s: SermonRow): string => (s.preachedOn ?? '').slice(0, 4);

/** Newest first; a sermon with no date yet goes last rather than vanishing. */
export const newestFirst = (a: SermonRow, b: SermonRow): number =>
  (b.preachedOn || '0000').localeCompare(a.preachedOn || '0000');

const fold = (s: string): string => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

function haystack(s: SermonRow): string {
  return fold([s.title, s.scripture, s.speaker, s.series, s.occasion, s.summary, yearOf(s)]
    .filter(Boolean).join(' '));
}

export function matches(s: SermonRow, f: SermonFilters): boolean {
  const any = (picked: string[], v: string | undefined) => !picked.length || picked.includes(v ?? '');
  if (!any(f.series, s.series) || !any(f.speakers, s.speaker)) return false;
  if (!any(f.years, yearOf(s)) || !any(f.occasions, s.occasion)) return false;
  const words = fold(f.q).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = haystack(s);
  return words.every((w) => hay.includes(w));
}

/** Words against a title alone — for YouTube's videos, which carry nothing else. */
export function titleMatches(title: string, q: string): boolean {
  const hay = fold(title);
  return fold(q).split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

/** Only the search is in use — the one thing a not-yet-entered YouTube video can answer. */
export const onlySearch = (f: SermonFilters): boolean =>
  Boolean(f.q.trim()) && !f.series.length && !f.speakers.length && !f.years.length && !f.occasions.length;

/** Only the values some sermon actually has: a filter option that finds nothing is a trap. */
export function used(sermons: readonly SermonRow[], pick: (s: SermonRow) => string | undefined): string[] {
  return [...new Set(sermons.map(pick).filter((v): v is string => Boolean(v?.trim())))];
}
